import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import BlockIcon from '@mui/icons-material/Block';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { AdminTaskRow, adminTaskApi } from '../../../../api/adminTaskApi';
import {
  AdminAdventureRow,
  adminAdventureApi,
} from '../../../../api/adminAdventureApi';
import {
  AdminCohortRow,
  adminCohortApi,
} from '../../../../api/adminCohortApi';
import {
  AdminSweepstakesRow,
  adminSweepstakesApi,
} from '../../../../api/adminSweepstakesApi';
import {
  AdminToSRow,
  adminToSApi,
} from '../../../../api/adminToSApi';
import {
  AdminQuestionnaireRow,
  adminQuestionnaireApi,
} from '../../../../api/adminQuestionnaireApi';
import {
  AdminCollectionRow,
  adminCollectionApi,
} from '../../../../api/adminCollectionApi';
import {
  AdminPlanCohortPurseMappingRow,
  adminPlanCohortPurseMappingApi,
} from '../../../../api/adminPlanCohortPurseMappingApi';
import {
  adminGlobalsApi,
  GlobalEntity,
} from '../../../../api/adminGlobalsApi';
import {
  adminCatalogApi,
  CatalogSlug,
} from '../../../../api/adminCatalogApi';
import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../../theme/tokens';
import { WriteButton } from '../../../../components/ui';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/**
 * What's being deactivated. Drives which backend client handles the
 * soft-delete, which dependency-scan entity slug we query, and whether
 * dependents make the dialog advisory (Variant B) or blocking (Variant C).
 *
 * - 'task' / 'adventure' — global entity; Variant B (advisory) when
 *   dependents exist. Deactivating with dependents is allowed because
 *   existing tenant references keep working.
 * - catalog kinds — deactivating a reference row that's still FK'd from
 *   live globals would orphan those globals. Variant C (blocking).
 */
export type DependencyImpactKind =
  | { entity: 'task'; taskCode: string; baseVersion: number }
  | { entity: 'adventure'; adventureCode: string; baseVersion: number }
  | { entity: 'cohort'; cohortCode: string; baseVersion: number }
  | { entity: 'sweepstakes'; sweepstakesCode: string; baseVersion: number }
  | { entity: 'terms_of_service'; tosCode: string; baseVersion: number }
  | { entity: 'questionnaire'; questionnaireCode: string; baseVersion: number }
  | { entity: 'task_reward_collection'; collectionCode: string; baseVersion: number }
  | { entity: 'plan_cohort_purse_mapping'; mappingCode: string; baseVersion: number }
  | {
      entity:
        | 'task_type'
        | 'task_category'
        | 'task_reward_type'
        | 'wallet_type'
        | 'component_type'
        | 'onboarding_component'
        | 'notification_channel'
        | 'notification_category'
        | 'canonical_field'
        | 'cohort_attribute_def';
      catalogSlug: CatalogSlug;
      code: string;
    };

export interface DependencyImpactDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Legacy shape — callers that haven't migrated yet pass these three
   * fields directly (task-only). If provided, `kind` is auto-built as
   * `{ entity: 'task', ... }`. Prefer `kind` for new call sites.
   */
  taskCode?: string;
  baseVersion?: number;
  /** Structured descriptor — new call sites. */
  kind?: DependencyImpactKind;
  /**
   * Pre-resolved dependents (optional — when absent we fetch via
   * `adminGlobalsApi.getDependencies`). Kept for tests and progressive
   * migration; leave undefined in real call sites.
   */
  dependents?: { tenant_code: string; reward_count: number }[];
  /**
   * Called on successful deactivate. The passed object always has `.code`
   * populated; task/adventure-specific fields (task_code, adventure_code,
   * current_version, etc.) are also mirrored on when known, so callers
   * written against the R3 task-only shape keep compiling.
   */
  onSuccess: (
    row: { code: string } & Partial<AdminTaskRow> &
      Partial<AdminAdventureRow> &
      Partial<AdminCohortRow> &
      Partial<AdminSweepstakesRow> &
      Partial<AdminToSRow> &
      Partial<AdminQuestionnaireRow> &
      Partial<AdminCollectionRow> &
      Partial<AdminPlanCohortPurseMappingRow>,
  ) => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function resolveKind(props: DependencyImpactDialogProps): DependencyImpactKind {
  if (props.kind) return props.kind;
  // Legacy task-only path.
  return {
    entity: 'task',
    taskCode: props.taskCode ?? '',
    baseVersion: props.baseVersion ?? 0,
  };
}

function entitySlugFor(kind: DependencyImpactKind): GlobalEntity {
  return kind.entity as GlobalEntity;
}

function codeFor(kind: DependencyImpactKind): string {
  if (kind.entity === 'task') return kind.taskCode;
  if (kind.entity === 'adventure') return kind.adventureCode;
  if (kind.entity === 'cohort') return kind.cohortCode;
  if (kind.entity === 'sweepstakes') return kind.sweepstakesCode;
  if (kind.entity === 'terms_of_service') return kind.tosCode;
  if (kind.entity === 'questionnaire') return kind.questionnaireCode;
  if (kind.entity === 'task_reward_collection') return kind.collectionCode;
  if (kind.entity === 'plan_cohort_purse_mapping') return kind.mappingCode;
  return kind.code;
}

const GLOBAL_ENTITIES = new Set([
  'task',
  'adventure',
  'cohort',
  'sweepstakes',
  'terms_of_service',
  'questionnaire',
  'task_reward_collection',
  'plan_cohort_purse_mapping',
]);

function isCatalogKind(kind: DependencyImpactKind): boolean {
  return !GLOBAL_ENTITIES.has(kind.entity);
}

function globalEntityNoun(kind: DependencyImpactKind): string {
  switch (kind.entity) {
    case 'task':
      return 'task';
    case 'adventure':
      return 'adventure';
    case 'cohort':
      return 'cohort';
    case 'sweepstakes':
      return 'sweepstake';
    case 'terms_of_service':
      return 'ToS version';
    case 'questionnaire':
      return 'questionnaire';
    case 'task_reward_collection':
      return 'collection';
    case 'plan_cohort_purse_mapping':
      return 'mapping';
    default:
      return 'entity';
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const DependencyImpactDialog: React.FC<DependencyImpactDialogProps> = (props) => {
  const { open, onClose, dependents: preResolvedDependents, onSuccess } = props;
  const kind = resolveKind(props);
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string }>({
    open: false,
    msg: '',
  });

  // R4: query the dependency-scan endpoint on dialog open. Disabled when
  // the caller pre-resolved `dependents` (tests + progressive migration).
  const scanQuery = useQuery({
    queryKey: ['admin', 'globals', kind.entity, codeFor(kind), 'dependencies'],
    queryFn: () => adminGlobalsApi.getDependencies(entitySlugFor(kind), codeFor(kind)),
    enabled: open && !preResolvedDependents,
    // Stale-time 0: dependency graphs shift quickly and a cached "0 deps"
    // shown on a second dialog-open after a refresh would be misleading.
    staleTime: 0,
  });

  const scanCount = preResolvedDependents
    ? preResolvedDependents.length
    : scanQuery.data?.count ?? 0;
  const hasDependents = scanCount > 0;
  const isBlockingKind = isCatalogKind(kind);

  // Variant C (catalog + dependents > 0): dialog is advisory-blocking.
  // Variant B (global + dependents > 0): warning + still allow deactivate.
  // Variant A (zero dependents): plain confirmation.
  const variant: 'A' | 'B' | 'C' = (() => {
    if (!hasDependents) return 'A';
    return isBlockingKind ? 'C' : 'B';
  })();

  const mutation = useMutation({
    mutationFn: async () => {
      if (kind.entity === 'task') {
        return adminTaskApi.update(kind.taskCode, {
          base_version: kind.baseVersion,
          active: false,
        });
      }
      if (kind.entity === 'adventure') {
        return adminAdventureApi.update(kind.adventureCode, {
          base_version: kind.baseVersion,
          active: false,
        });
      }
      if (kind.entity === 'cohort') {
        return adminCohortApi.update(kind.cohortCode, {
          base_version: kind.baseVersion,
          active: false,
        });
      }
      if (kind.entity === 'sweepstakes') {
        return adminSweepstakesApi.update(kind.sweepstakesCode, {
          base_version: kind.baseVersion,
          active: false,
        });
      }
      if (kind.entity === 'terms_of_service') {
        return adminToSApi.update(kind.tosCode, {
          base_version: kind.baseVersion,
          active: false,
        });
      }
      if (kind.entity === 'questionnaire') {
        return adminQuestionnaireApi.update(kind.questionnaireCode, {
          base_version: kind.baseVersion,
          active: false,
        });
      }
      if (kind.entity === 'task_reward_collection') {
        return adminCollectionApi.update(kind.collectionCode, {
          base_version: kind.baseVersion,
          active: false,
        });
      }
      if (kind.entity === 'plan_cohort_purse_mapping') {
        return adminPlanCohortPurseMappingApi.update(kind.mappingCode, {
          base_version: kind.baseVersion,
          active: false,
        });
      }
      // Catalog soft-delete — re-uses the active flag on the row.
      return adminCatalogApi.update(kind.catalogSlug, kind.code, { active: false });
    },
    onSuccess: (row) => {
      // Update calls return slightly different row shapes; normalize to
      // something with a `.code` the caller can snackbar. Each global
      // entity exposes its own `<entity>_code` field we need to surface.
      const anyRow = row as any;
      const codeOnRow =
        anyRow?.task_code ??
        anyRow?.adventure_code ??
        anyRow?.cohort_code ??
        anyRow?.sweepstakes_code ??
        anyRow?.tos_code ??
        anyRow?.questionnaire_code ??
        anyRow?.collection_code ??
        anyRow?.mapping_code ??
        anyRow?.code ??
        codeFor(kind);
      onSuccess({ code: codeOnRow, ...anyRow });
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  /* ---------- Deep-link dependents ---------- */

  const handleOpenDependents = () => {
    // Only defined for global entities (tasks/adventures). Drops into a
    // filtered list view if a known route exists; otherwise surfaces a
    // snackbar pointing at R5.
    const code = codeFor(kind);
    const filter = `refs:${encodeURIComponent(code)}`;
    if (kind.entity === 'task') {
      navigate(`/admin/global-catalog/tasks?filter=${filter}`);
      return;
    }
    if (kind.entity === 'adventure') {
      navigate(`/admin/global-catalog/adventures?filter=${filter}`);
      return;
    }
    if (kind.entity === 'cohort') {
      navigate(`/admin/global-catalog/cohorts?filter=${filter}`);
      return;
    }
    if (kind.entity === 'sweepstakes') {
      navigate(`/admin/global-catalog/sweepstakes?filter=${filter}`);
      return;
    }
    if (kind.entity === 'terms_of_service') {
      navigate(`/admin/global-catalog/terms-of-service?filter=${filter}`);
      return;
    }
    if (kind.entity === 'questionnaire') {
      navigate(`/admin/global-catalog/questionnaires?filter=${filter}`);
      return;
    }
    if (kind.entity === 'task_reward_collection') {
      navigate(`/admin/global-catalog/collections?filter=${filter}`);
      return;
    }
    if (kind.entity === 'plan_cohort_purse_mapping') {
      navigate(`/admin/global-catalog/plan-cohort-purse?filter=${filter}`);
      return;
    }
    setSnackbar({ open: true, msg: 'Full list drawer lands in a later round' });
  };

  /* ---------- Preview list (first 5) ---------- */

  const previewRows = (() => {
    if (preResolvedDependents) {
      return preResolvedDependents.slice(0, 5).map((d) => ({
        name: d.tenant_code,
        tenant_code: d.tenant_code,
        subtitle: `${d.reward_count} reward${d.reward_count === 1 ? '' : 's'}`,
      }));
    }
    return (scanQuery.data?.preview ?? []).slice(0, 5).map((d) => ({
      name: d.name || d.tenant_code,
      tenant_code: d.tenant_code,
      subtitle: d.kind
        ? `${d.kind}${d.reference_count && d.reference_count > 1 ? ` × ${d.reference_count}` : ''}`
        : d.reference_count && d.reference_count > 1
          ? `× ${d.reference_count}`
          : null,
    }));
  })();
  const remaining = Math.max(0, scanCount - previewRows.length);

  /* ---------- Loading state ---------- */
  if (open && scanQuery.isLoading && !preResolvedDependents) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          Checking dependents…
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress sx={{ color: colors.adminAccent }} />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  /* ---------- Variant C — blocking (catalog rows with refs) ---------- */
  if (variant === 'C') {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth role="alertdialog">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 700,
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          <BlockIcon sx={{ color: colors.errorIcon }} />
          Cannot deactivate{' '}
          <Box component="code" sx={{ ...typo.mono, fontSize: 14 }}>
            {codeFor(kind)}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
            {scanCount} global {scanCount === 1 ? 'entity references' : 'entities reference'} this
            row. Remove or repoint {scanCount === 1 ? 'it' : 'them'} before deactivating.
          </Alert>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {previewRows.map((d) => (
              <Box
                component="li"
                key={`${d.tenant_code}:${d.name}`}
                sx={{ fontSize: 13, color: colors.textPrimary, mb: 0.5 }}
              >
                <Box component="code" sx={{ ...typo.mono, fontSize: 12 }}>
                  {d.name}
                </Box>
                {d.subtitle && (
                  <Box component="span" sx={{ color: colors.textTertiary, ml: 0.75 }}>
                    — {d.subtitle}
                  </Box>
                )}
              </Box>
            ))}
            {remaining > 0 && (
              <Typography
                component="li"
                sx={{
                  fontSize: 13,
                  color: colors.textTertiary,
                  fontStyle: 'italic',
                  listStyle: 'none',
                  pl: 0,
                }}
              >
                …and {remaining} more
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} sx={primaryButtonSx}>
            Close
          </Button>
        </DialogActions>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity="info"
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            sx={{ borderRadius: '8px' }}
          >
            {snackbar.msg}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  }

  /* ---------- Variant B — warning but allow override ---------- */
  if (variant === 'B') {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth role="alertdialog">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 700,
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          <WarningAmberIcon sx={{ color: colors.warningIcon }} />
          Deactivate{' '}
          <Box component="code" sx={{ ...typo.mono, fontSize: 14 }}>
            {codeFor(kind)}
          </Box>
          ?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
            {scanCount} {scanCount === 1 ? 'tenant references' : 'tenants reference'} this{' '}
            {globalEntityNoun(kind)}.
          </Alert>
          <DialogContentText sx={{ fontSize: 13, color: colors.textSecondary, mb: 2 }}>
            Dependents keep referencing it while it&apos;s inactive. Existing tenant rules
            won&apos;t break, but no new tenants will see this {globalEntityNoun(kind)}.
          </DialogContentText>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {previewRows.map((d) => (
              <Box
                component="li"
                key={`${d.tenant_code}:${d.name}`}
                sx={{ fontSize: 13, color: colors.textPrimary, mb: 0.5 }}
              >
                <Box component="code" sx={{ ...typo.mono, fontSize: 12 }}>
                  {d.name}
                </Box>
                {d.subtitle && (
                  <Box component="span" sx={{ color: colors.textTertiary, ml: 0.75 }}>
                    — {d.subtitle}
                  </Box>
                )}
              </Box>
            ))}
            {remaining > 0 && (
              <Typography
                component="li"
                sx={{
                  fontSize: 13,
                  color: colors.textTertiary,
                  fontStyle: 'italic',
                  listStyle: 'none',
                  pl: 0,
                }}
              >
                …and {remaining} more
              </Typography>
            )}
          </Box>
          {mutation.isError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
              Deactivate failed — try again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} sx={secondaryButtonSx} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Tooltip title="Open the filtered dependents view">
            <span>
              <Button onClick={handleOpenDependents} sx={secondaryButtonSx}>
                Open dependents
              </Button>
            </span>
          </Tooltip>
          <WriteButton
            variant="contained"
            color="error"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Deactivating…' : 'Deactivate anyway'}
          </WriteButton>
        </DialogActions>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity="info"
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            sx={{ borderRadius: '8px' }}
          >
            {snackbar.msg}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  }

  /* ---------- Variant A — default no-dependents confirmation ---------- */
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
        Deactivate{' '}
        <Box component="code" sx={{ ...typo.mono, fontSize: 14 }}>
          {codeFor(kind)}
        </Box>
        ?
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 13, color: colors.textSecondary }}>
          It will be hidden from tenants on next sync. You can reactivate later from the{' '}
          {isCatalogKind(kind) ? '' : globalEntityNoun(kind)}&apos;s edit page.
        </DialogContentText>
        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
            Deactivate failed — try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={secondaryButtonSx} disabled={mutation.isPending}>
          Cancel
        </Button>
        <WriteButton
          variant="contained"
          color="error"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Deactivating…' : 'Deactivate'}
        </WriteButton>
      </DialogActions>
    </Dialog>
  );
};

export default DependencyImpactDialog;
